// Phase HR-003 Wave 6 — Production walk: Work sampling + Stoppage events.
//
// IE / Production-owned. Doc Studies #17 (Work Sampling), #18 (Idle Time),
// #22 (Micro-stop). Two tabs:
//
//   Sampling — quick tap to record "worker X was [activity] at this moment"
//              over many ticks → productive % distribution.
//
//   Stoppage — Start a stoppage when a machine is observed idle, pick the
//              reason chip, End it later (or set ended_at immediately if
//              short). Auto-classified as MICRO_STOP if < 10 min.

import { useMemo, useState } from 'react'
import {
  Alert, App, Button, DatePicker, Empty, Form, Input, List, Modal,
  Select, Space, Tabs, Tag, Typography,
} from 'antd'
import {
  ClockCircleOutlined, FieldTimeOutlined, PlusOutlined, StopOutlined,
} from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import {
  floorApi, type ActivityCode, type StoppageEvent, type StoppageReason,
  type StoppageType,
} from '@/features/hr/trainingFloorApi'
import { departmentsApi } from '@/features/masters/departments/api'
import { employeesApi } from '@/features/hr/api'
import { machinesApi } from '@/features/masters/machines/api'
import PageHeader from '@/shared/components/PageHeader'
import { usePlantId } from '@/shared/hooks/usePlantId'
import { useHrMobilePolish, type ThresholdResult } from '@/shared/styles/hrMobilePolish'

dayjs.extend(relativeTime)
const { Text } = Typography

const ACTIVITY_CHIPS: { code: ActivityCode; label: string; tone: ThresholdResult }[] = [
  { code: 'PRODUCTIVE',          label: 'Productive',   tone: 'green' },
  { code: 'WAITING_MATERIAL',    label: 'Waiting mat.', tone: 'red' },
  { code: 'WAITING_INSTRUCTION', label: 'Waiting inst', tone: 'red' },
  { code: 'WAITING_FITTER',      label: 'Waiting fit',  tone: 'red' },
  { code: 'WALKING',             label: 'Walking',      tone: 'amber' },
  { code: 'CLEANING',            label: 'Cleaning',     tone: 'amber' },
  { code: 'IDLE',                label: 'Idle',         tone: 'red' },
  { code: 'BREAK',               label: 'Break',        tone: 'neutral' },
  { code: 'TALKING',             label: 'Talking',      tone: 'amber' },
  { code: 'OTHER',               label: 'Other',        tone: 'neutral' },
]

const STOPPAGE_REASON_CHIPS: { code: StoppageReason; label: string }[] = [
  { code: 'NO_MATERIAL',    label: 'No material' },
  { code: 'NO_OPERATOR',    label: 'No operator' },
  { code: 'NO_FITTER',      label: 'No fitter' },
  { code: 'NO_QUALITY_OK',  label: 'Quality not OK' },
  { code: 'CHANGEOVER',     label: 'Changeover' },
  { code: 'BREAKDOWN',      label: 'Breakdown' },
  { code: 'POWER',          label: 'Power' },
  { code: 'STARVATION',     label: 'Starvation' },
  { code: 'CLEANING',       label: 'Cleaning' },
  { code: 'SHIFT_HANDOVER', label: 'Handover' },
  { code: 'OTHER',          label: 'Other' },
]

export default function ProductionWalkPage() {
  useHrMobilePolish()
  const [tab, setTab] = useState<'sampling' | 'stoppage'>('sampling')
  return (
    <div className="hr-mobile-root">
      <PageHeader
        title="Production Walk"
        subtitle="Doc §17/18/22 — work sampling + idle + micro-stop, captured in seconds."
      />
      <Tabs activeKey={tab} onChange={(k) => setTab(k as typeof tab)}
        items={[
          { key: 'sampling', label: <span><ClockCircleOutlined /> Work sampling</span>,
            children: <SamplingTab /> },
          { key: 'stoppage', label: <span><StopOutlined /> Stoppage events</span>,
            children: <StoppageTab /> },
        ]} />
    </div>
  )
}

// ═══ Work Sampling tab ═════════════════════════════════════════════

function SamplingTab() {
  const { message } = App.useApp()
  const qc = useQueryClient()
  const { plantId } = usePlantId()
  const [deptId, setDeptId] = useState<string | undefined>()
  const [empId, setEmpId] = useState<string | undefined>()
  const [open, setOpen] = useState(false)
  const [activity, setActivity] = useState<ActivityCode | null>(null)
  const [remark, setRemark] = useState('')

  const { data: deptsData } = useQuery({
    queryKey: ['pw-depts', plantId],
    queryFn: () => departmentsApi.list({ page: 1, page_size: 500, plant_id: plantId }),
    enabled: !!plantId,
  })
  const { data: empsData } = useQuery({
    queryKey: ['pw-emps', plantId, deptId],
    queryFn: () => employeesApi.list({
      plant_id: plantId, department_id: deptId,
      is_active: true, page: 1, size: 500,
    }),
    enabled: !!plantId,
  })
  const { data: dist } = useQuery({
    queryKey: ['pw-dist', plantId, deptId],
    queryFn: () => floorApi.distribution({
      plant_id: plantId, department_id: deptId,
      from_dt: dayjs().startOf('day').toISOString(),
    }),
    enabled: !!plantId,
  })
  const { data: samples = [] } = useQuery({
    queryKey: ['pw-samples', plantId, deptId],
    queryFn: () => floorApi.listSamples({
      plant_id: plantId, department_id: deptId,
      from_dt: dayjs().startOf('day').toISOString(),
      limit: 50,
    }),
    enabled: !!plantId,
  })

  const saveMut = useMutation({
    mutationFn: () => {
      if (!plantId) throw new Error('Plant missing')
      if (!activity) throw new Error('Pick an activity')
      return floorApi.createSample({
        plant_id: plantId, activity_code: activity,
        department_id: deptId || null,
        observed_employee_id: empId || null,
        remark: remark || null,
      })
    },
    onSuccess: () => {
      message.success('Sample recorded.')
      qc.invalidateQueries({ queryKey: ['pw-samples'] })
      qc.invalidateQueries({ queryKey: ['pw-dist'] })
      setOpen(false); setActivity(null); setRemark(''); setEmpId(undefined)
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (e: any) => message.error(e?.message ?? e?.response?.data?.detail ?? 'Failed'),
  })

  return (
    <>
      <Space wrap style={{ marginBottom: 12 }}>
        <Select allowClear placeholder="Department" style={{ minWidth: 200 }}
          value={deptId} onChange={setDeptId}
          options={(deptsData?.items ?? []).map((d) => ({ label: d.name, value: d.id }))} />
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setOpen(true)}>
          Tick a sample
        </Button>
      </Space>

      {/* Distribution tiles */}
      <div style={{
        display: 'grid', gap: 8, marginBottom: 12,
        gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
      }}>
        <KPITile
          variant={(dist?.productive_pct ?? 0) >= 80 ? 'green'
                 : (dist?.productive_pct ?? 0) >= 65 ? 'amber' : 'red'}
          value={dist?.productive_pct == null ? '—' : `${dist.productive_pct}%`}
          label="Productive (today)" />
        <KPITile variant="neutral" value={dist?.total ?? 0} label="Samples today" />
      </div>

      {samples.length === 0 ? <Empty description="No samples yet today." image={Empty.PRESENTED_IMAGE_SIMPLE} />
      : (
        <List dataSource={samples} size="small"
          renderItem={(s) => (
            <List.Item>
              <List.Item.Meta
                title={<Space>
                  <Tag color={
                    ACTIVITY_CHIPS.find(a => a.code === s.activity_code)?.tone === 'red' ? 'red'
                    : ACTIVITY_CHIPS.find(a => a.code === s.activity_code)?.tone === 'amber' ? 'gold'
                    : ACTIVITY_CHIPS.find(a => a.code === s.activity_code)?.tone === 'green' ? 'green'
                    : 'default'
                  }>{s.activity_code}</Tag>
                  <Text>{s.observed_employee_name ?? '—'}</Text>
                  {s.department_name && <Text type="secondary">· {s.department_name}</Text>}
                </Space>}
                description={
                  <Text style={{ fontSize: 11, color: '#8c8c8c' }}>
                    {dayjs(s.sample_at).fromNow()}
                    {s.remark && ` · ${s.remark}`}
                  </Text>
                }
              />
            </List.Item>
          )} />
      )}

      <Modal open={open} onCancel={() => setOpen(false)}
        title="New work-sample tick" width={480}
        onOk={() => saveMut.mutate()} confirmLoading={saveMut.isPending}
        okButtonProps={{ disabled: !activity }} okText="Save tick">
        <Form layout="vertical">
          <Form.Item label="Worker">
            <Select allowClear showSearch placeholder="Pick worker (optional)"
              filterOption={(input, opt) =>
                (opt?.label as string)?.toLowerCase().includes(input.toLowerCase())}
              options={(empsData?.items ?? []).map((e) => ({
                value: e.id, label: `${e.emp_name} (${e.emp_code})`,
              }))}
              value={empId} onChange={setEmpId} />
          </Form.Item>
          <Form.Item label="Activity">
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              {ACTIVITY_CHIPS.map((a) => (
                <button key={a.code} type="button"
                  className={`hr-filter-chip${activity === a.code ? ' hr-filter-chip--active' : ''}`}
                  onClick={() => setActivity(a.code)}>{a.label}</button>
              ))}
            </div>
          </Form.Item>
          <Form.Item label="Remark">
            <Input value={remark} onChange={(e) => setRemark(e.target.value)}
              placeholder="Optional" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  )
}

// ═══ Stoppage tab ══════════════════════════════════════════════════

function StoppageTab() {
  const { message } = App.useApp()
  const qc = useQueryClient()
  const { plantId } = usePlantId()
  const [open, setOpen] = useState(false)
  const [endingId, setEndingId] = useState<string | null>(null)
  const [endReason, setEndReason] = useState<StoppageReason | null>(null)
  const [endText, setEndText] = useState('')

  const { data: machinesData } = useQuery({
    queryKey: ['pw-machines', plantId],
    queryFn: () => machinesApi.list({ plant_id: plantId, is_active: true,
                                       page: 1, page_size: 500 }),
    enabled: !!plantId,
  })
  const { data: openRows = [] } = useQuery({
    queryKey: ['pw-stoppages-open', plantId],
    queryFn: () => floorApi.listStoppages({
      plant_id: plantId, open_only: true, limit: 50,
    }),
    enabled: !!plantId,
  })
  const { data: recentRows = [] } = useQuery({
    queryKey: ['pw-stoppages-recent', plantId],
    queryFn: () => floorApi.listStoppages({
      plant_id: plantId, from_dt: dayjs().subtract(2, 'day').toISOString(),
      limit: 100,
    }),
    enabled: !!plantId,
  })

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['pw-stoppages-open'] })
    qc.invalidateQueries({ queryKey: ['pw-stoppages-recent'] })
  }

  const endMut = useMutation({
    mutationFn: (id: string) => floorApi.endStoppage(id, {
      ended_at: new Date().toISOString(),
      reason_code: endReason,
      reason_text: endText || null,
    }),
    onSuccess: () => {
      message.success('Stoppage ended.')
      refresh()
      setEndingId(null); setEndReason(null); setEndText('')
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (e: any) => message.error(e?.response?.data?.detail ?? 'Failed'),
  })

  return (
    <>
      <Space wrap style={{ marginBottom: 12 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setOpen(true)}>
          Start stoppage
        </Button>
      </Space>

      <Text strong>Open stoppages ({openRows.length})</Text>
      {openRows.length === 0 ? <Empty description="None open." image={Empty.PRESENTED_IMAGE_SIMPLE} />
      : (
        <div style={{
          display: 'grid', gap: 8, marginTop: 8,
          gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
        }}>
          {openRows.map((s) => (
            <StoppageCard key={s.id} row={s}
              ending={endingId === s.id}
              endReason={endReason} setEndReason={setEndReason}
              endText={endText} setEndText={setEndText}
              onEndClick={() => setEndingId(s.id)}
              onConfirmEnd={() => endMut.mutate(s.id)}
              onCancel={() => { setEndingId(null); setEndReason(null); setEndText('') }}
              ending_loading={endMut.isPending} />
          ))}
        </div>
      )}

      <div style={{ marginTop: 16 }}>
        <Text strong>Recent (last 48h)</Text>
        <List dataSource={recentRows.filter((r) => r.ended_at)} size="small"
          renderItem={(s) => (
            <List.Item>
              <List.Item.Meta
                title={<Space>
                  <Tag color={s.stoppage_type === 'MICRO_STOP' ? 'gold' : 'red'}>
                    {s.stoppage_type}
                  </Tag>
                  {s.machine_code && <Tag>{s.machine_code}</Tag>}
                  {s.reason_code && <Tag>{s.reason_code}</Tag>}
                  <Text>{s.duration_minutes != null
                    ? `${s.duration_minutes.toFixed(1)} min` : '—'}</Text>
                </Space>}
                description={<Text style={{ fontSize: 11, color: '#8c8c8c' }}>
                  {dayjs(s.started_at).fromNow()}
                  {s.reason_text && ` · ${s.reason_text}`}
                </Text>}
              />
            </List.Item>
          )} />
      </div>

      <StoppageStartModal open={open} onClose={() => setOpen(false)}
        machines={(machinesData?.items ?? []).map((m) => ({
          value: m.id,
          label: `${m.machine_id_str} · ${m.name} (${m.section})`,
        }))}
        onSaved={refresh} />
    </>
  )
}

function StoppageCard({ row, ending, endReason, setEndReason,
                        endText, setEndText, onEndClick,
                        onConfirmEnd, onCancel, ending_loading }: {
  row: StoppageEvent; ending: boolean
  endReason: StoppageReason | null
  setEndReason: (r: StoppageReason | null) => void
  endText: string; setEndText: (s: string) => void
  onEndClick: () => void; onConfirmEnd: () => void
  onCancel: () => void; ending_loading: boolean
}) {
  const ongoingMin = (Date.now() - new Date(row.started_at).getTime()) / 60000
  const tone: ThresholdResult = ongoingMin > 30 ? 'red'
                              : ongoingMin > 10 ? 'amber' : 'neutral'
  const stripe = tone === 'red' ? '#cf1322' : tone === 'amber' ? '#d48806' : undefined
  return (
    <div className="hr-roster-card"
      style={{ flexDirection: 'column', alignItems: 'stretch',
                borderLeft: stripe ? `4px solid ${stripe}` : undefined }}>
      <Space wrap>
        <Tag color={row.stoppage_type === 'MICRO_STOP' ? 'gold' : 'red'}>
          {row.stoppage_type}
        </Tag>
        {row.machine_code && <Tag>{row.machine_code}</Tag>}
        <Text strong>{ongoingMin.toFixed(0)} min</Text>
        <FieldTimeOutlined />
      </Space>
      {row.reason_code && (
        <Text type="secondary" style={{ fontSize: 12, marginTop: 4 }}>
          Reason: {row.reason_code}
        </Text>
      )}
      {!ending ? (
        <Button size="small" type="primary" icon={<StopOutlined />}
          style={{ marginTop: 8 }} onClick={onEndClick}>End stoppage</Button>
      ) : (
        <div style={{ marginTop: 8 }}>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 6 }}>
            {STOPPAGE_REASON_CHIPS.map((r) => (
              <button key={r.code} type="button"
                className={`hr-filter-chip${endReason === r.code ? ' hr-filter-chip--active' : ''}`}
                style={{ height: 26, padding: '0 8px', fontSize: 11 }}
                onClick={() => setEndReason(endReason === r.code ? null : r.code)}>
                {r.label}
              </button>
            ))}
          </div>
          <Space.Compact style={{ width: '100%' }}>
            <Input placeholder="Notes (optional)" value={endText}
              onChange={(e) => setEndText(e.target.value)} />
            <Button type="primary" loading={ending_loading}
              onClick={onConfirmEnd}>Save</Button>
            <Button onClick={onCancel}>Cancel</Button>
          </Space.Compact>
        </div>
      )}
    </div>
  )
}

function StoppageStartModal({ open, onClose, machines, onSaved }: {
  open: boolean; onClose: () => void
  machines: { value: string; label: string }[]
  onSaved: () => void
}) {
  const { message } = App.useApp()
  const { plantId } = usePlantId()
  const [type, setType] = useState<StoppageType>('IDLE')
  const [machineId, setMachineId] = useState<string | undefined>()
  const [reason, setReason] = useState<StoppageReason | null>(null)
  const [reasonText, setReasonText] = useState('')

  useMemo(() => {
    if (open) {
      setType('IDLE'); setMachineId(undefined)
      setReason(null); setReasonText('')
    }
  }, [open])

  const saveMut = useMutation({
    mutationFn: () => {
      if (!plantId) throw new Error('Plant missing')
      if (!machineId) throw new Error('Pick a machine')
      return floorApi.createStoppage({
        plant_id: plantId, machine_id: machineId,
        stoppage_type: type, started_at: new Date().toISOString(),
        reason_code: reason, reason_text: reasonText || null,
      })
    },
    onSuccess: () => {
      message.success('Stoppage started.')
      onSaved(); onClose()
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (e: any) => message.error(e?.message ?? e?.response?.data?.detail ?? 'Failed'),
  })

  return (
    <Modal open={open} onCancel={onClose}
      title="Start stoppage" width={480}
      onOk={() => saveMut.mutate()} confirmLoading={saveMut.isPending}
      okButtonProps={{ disabled: !machineId }} okText="Start">
      <Form layout="vertical">
        <Form.Item label="Type">
          <Space>
            <Button type={type === 'IDLE' ? 'primary' : 'default'}
              onClick={() => setType('IDLE')}>Idle</Button>
            <Button type={type === 'MICRO_STOP' ? 'primary' : 'default'}
              onClick={() => setType('MICRO_STOP')}>Micro-stop</Button>
          </Space>
        </Form.Item>
        <Form.Item label="Machine" required>
          <Select showSearch placeholder="Pick a machine"
            filterOption={(input, opt) =>
              (opt?.label as string)?.toLowerCase().includes(input.toLowerCase())}
            options={machines} value={machineId} onChange={setMachineId} />
        </Form.Item>
        <Form.Item label="Reason (optional now, required at end)">
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {STOPPAGE_REASON_CHIPS.map((r) => (
              <button key={r.code} type="button"
                className={`hr-filter-chip${reason === r.code ? ' hr-filter-chip--active' : ''}`}
                onClick={() => setReason(reason === r.code ? null : r.code)}>
                {r.label}
              </button>
            ))}
          </div>
        </Form.Item>
        <Form.Item label="Reason notes">
          <Input value={reasonText} onChange={(e) => setReasonText(e.target.value)}
            placeholder="Optional context" />
        </Form.Item>
      </Form>
      <Alert showIcon
        message="Started time is now. End it later from the open stoppages list — duration auto-computes." />
    </Modal>
  )
}

// ─── Shared ────────────────────────────────────────────────────────

function KPITile({ variant, value, label }: {
  variant: ThresholdResult; value: number | string; label: string
}) {
  return (
    <div className={`threshold-tile threshold-tile--${variant}`}>
      <div className="threshold-tile__value">{value}</div>
      <div className="threshold-tile__label">{label}</div>
    </div>
  )
}
